import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { dataHora, plural, telefoneLegivel } from '../../formato';
import { useSessao } from '../../sessao';
import { CONEXAO_DO_CANAL, degrauDe, SITUACAO_DO_CANAL } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Area, Campo, Carregando, Dialogo, ErroDaApi, RespostaDaAcao, Selo, Vazio } from '../../ui';
import { reais } from './crm';
import { faltaPara, Rodape } from './JanelasDaConversa';
import './ajustes.css';

/**
 * Os ajustes do Comercial — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa
 * 16). `/comercial/ajustes`, no Design Moderno (RN-269).
 *
 * Nove abas, cada uma com o endereço dela (`?aba=`): Canais, Rotas e
 * substitutos, Horário, Números internos, Respostas prontas, Estimativa,
 * Estratégicos (só para quem marca o cliente estratégico), Contatos com o
 * mesmo telefone e Entradas com falha.
 *
 *  · **Canais:** a situação e a conexão de cada canal em `Selo`; o QR ao
 *    vivo pelo tempo real (`comercial.canais`), com a frase de onde ler, só
 *    para quem liga canais (o QR liga o número a um celular); pôr no ar, em
 *    teste, tirar do ar e desconectar o aparelho abrem a janela do motivo
 *    (10 caracteres ou mais, P-TXT-10). Nenhuma ação primária na aba: cada
 *    canal muda pela janela dele.
 *  · **As abas de formulário** (rotas, horário, números, estimativa) têm UM
 *    «Gravar», o único primário da aba; quem só vê lê os valores.
 *  · Nenhum código de permissão na tela (P-TXT-03); a recusa vem da API, em
 *    três partes.
 */

type Aba = 'canais' | 'rotas' | 'horario' | 'numeros' | 'respostas' | 'estimativa' | 'estrategicos'
  | 'repetidos' | 'falhas';

const ABAS: { chave: Aba; texto: string }[] = [
  { chave: 'canais', texto: 'Canais' },
  { chave: 'rotas', texto: 'Rotas e substitutos' },
  { chave: 'horario', texto: 'Horário' },
  { chave: 'numeros', texto: 'Números internos' },
  { chave: 'respostas', texto: 'Respostas prontas' },
  { chave: 'estimativa', texto: 'Estimativa' },
  { chave: 'estrategicos', texto: 'Estratégicos' },
  { chave: 'repetidos', texto: 'Contatos com o mesmo telefone' },
  { chave: 'falhas', texto: 'Entradas com falha' },
];

interface Pessoa { id: string; nome: string }

/** O formatador mora no `formato.ts` (onda 552); o nome segue exportado daqui. */
export { telefoneLegivel };

/** A frase de quem só vê: quem muda, sem o nome da permissão. */
const SO_VE = 'Só quem configura o atendimento muda estes ajustes; aqui você os consulta.';

export function AjustesComercial() {
  const { pode } = useSessao();
  const [busca, setBusca] = useSearchParams();
  const marcaEstrategico = pode('cliente.marcar_estrategico');
  const abas = ABAS.filter((a) => a.chave !== 'estrategicos' || marcaEstrategico);
  const pedida = busca.get('aba');
  const aba: Aba = abas.find((a) => a.chave === pedida)?.chave ?? 'canais';
  const ir = (a: Aba) => {
    const b = new URLSearchParams(busca);
    if (a === 'canais') b.delete('aba'); else b.set('aba', a);
    setBusca(b, { replace: true });
  };

  return (
    <div className="ajc">
      <header className="cabeca">
        <h1>Ajustes do Comercial</h1>
        <p className="sub">Os canais, quem recebe cada assunto, o horário e as regras do atendimento.</p>
      </header>
      <div className="abas ajc-abas" role="tablist" aria-label="Ajustes do Comercial">
        {abas.map((a) => (
          <button key={a.chave} type="button" role="tab" aria-selected={aba === a.chave}
            className={aba === a.chave ? 'ativa' : undefined} onClick={() => ir(a.chave)}>
            {a.texto}
          </button>
        ))}
      </div>
      <section className="ajc-painel" role="tabpanel" aria-label={abas.find((a) => a.chave === aba)?.texto}>
        {aba === 'canais' && <AbaCanais />}
        {aba === 'rotas' && <AbaRotas />}
        {aba === 'horario' && <AbaHorario />}
        {aba === 'numeros' && <AbaNumeros />}
        {aba === 'respostas' && <AbaRespostas />}
        {aba === 'estimativa' && <AbaEstimativa />}
        {aba === 'estrategicos' && <AbaEstrategicos />}
        {aba === 'repetidos' && <AbaRepetidos />}
        {aba === 'falhas' && <AbaFalhas />}
      </section>
    </div>
  );
}

/** Carrega uma rota da API e devolve o dado, o erro e o «de novo». */
function useCarga<T>(rota: string) {
  const [dado, setDado] = useState<T | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const vez = useRef(0);
  const carregar = useCallback(async () => {
    const n = ++vez.current;
    try {
      const r = await api<T>(rota);
      if (n !== vez.current) return;
      setDado(r);
      setErro(null);
    } catch (e) {
      if (n === vez.current) setErro(e);
    }
  }, [rota]);
  useEffect(() => { void carregar(); }, [carregar]);
  return { dado, setDado, erro, carregar };
}

/** O cartão da aba, com o título, a frase e o conteúdo. */
function Cartao({ titulo, frase, children, acoes }: { titulo: string; frase?: ReactNode; children: ReactNode; acoes?: ReactNode }) {
  return (
    <div className="cartao ajc-cartao">
      <div className="cartao-cabeca">
        <h2>{titulo}</h2>
        {acoes}
      </div>
      {frase && <p className="ajc-frase">{frase}</p>}
      {children}
    </div>
  );
}

/** O rodapé de gravar da aba: o único primário dela; indisponível, diz por quê. */
function Gravar({ mudou, ocupado, aoGravar }: { mudou: boolean; ocupado: boolean; aoGravar(): void }) {
  return (
    <div className="ajc-gravar">
      {!mudou && <span className="o-que-falta">Nada mudou ainda.</span>}
      <button type="button" className="botao" aria-disabled={!mudou || ocupado}
        onClick={() => { if (mudou && !ocupado) aoGravar(); }}>
        {ocupado ? 'Gravar…' : 'Gravar'}
      </button>
    </div>
  );
}

// ── A janela do motivo ─────────────────────────────────────────────────

/**
 * Pôr no ar, em teste, tirar do ar, desconectar o aparelho, marcar o
 * estratégico: o motivo (10 caracteres ou mais) vai à trilha. A janela é a
 * confirmação — nada de `confirm()` do navegador.
 */
function JanelaDoMotivo({ aberta, titulo, rotulo, explica, acao, perigo = false, aoFechar, aoAgir }: {
  aberta: boolean; titulo: string; rotulo?: string; explica: ReactNode; acao: string; perigo?: boolean;
  aoFechar(): void; aoAgir(motivo: string): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { if (aberta) { setMotivo(''); setErro(null); setOcupado(false); } }, [aberta]);
  const faltam = 10 - motivo.trim().length;
  const falta = faltaPara('gravar', faltam > 0 ? ['o motivo, com 10 caracteres ou mais'] : []);
  const agir = async () => {
    setOcupado(true);
    setErro(null);
    try {
      await aoAgir(motivo.trim());
    } catch (e) {
      setErro(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto={aberta} titulo={titulo} rotulo={rotulo} aoFechar={aoFechar}>
      <div className="ajc-janela">
        <div className="ajc-explica">{explica}</div>
        <Area rotulo="Motivo" value={motivo} rows={3} maxLength={500} onChange={(e) => setMotivo(e.target.value)}
          dica={faltam > 0 ? `Fica na trilha. Faltam ${plural(faltam, 'caractere', 'caracteres')}.` : 'Fica na trilha.'} />
        {erro ? <ErroDaApi erro={erro} /> : null}
      </div>
      <Rodape acao={acao} falta={falta} ocupado={ocupado} perigo={perigo} aoCancelar={aoFechar} aoAgir={() => void agir()} />
    </Dialogo>
  );
}

// ── Canais ─────────────────────────────────────────────────────────────

interface Canal {
  codigo: string; nome: string; tipo: string; conexao: string; situacao: string; situacaoConexao: string;
  identificador: string | null; agenteAtivo: boolean; visibilidade: string; enviaAvisos: boolean;
  dono: Pessoa | null; aguardaQr: boolean; qr: { imagem: string; em: string } | null;
  desconexao: { pedidaEm: string; pedidaPor: string } | null;
  /** O celular conectado, como a mensageria disse (R5 m-6). `conferido` nulo: o canal não tem número. */
  aparelho: { telefone: string | null; conferido: boolean | null } | null;
}

type Pedido = { canal: Canal; tipo: 'no_ar' | 'teste' | 'desligar' | 'desconectar' } | null;

const TIPO_DO_CANAL: { readonly [t: string]: string } = {
  whatsapp: 'WhatsApp', email: 'E-mail', site: 'Formulário do site', telefone: 'Telefone',
};
const CONEXAO: { readonly [t: string]: string } = {
  qr: 'por QR code', api: 'pela API oficial', imap: 'IMAP e SMTP', formulario: 'pelo site', manual: 'registro à mão',
};

function AbaCanais() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const liga = pode('canal.ligar');
  const { dado, setDado, erro, carregar } = useCarga<{ canais: Canal[]; pessoas: Pessoa[] }>('/comercial/ajustes/canais');
  const [pedido, setPedido] = useState<Pedido>(null);
  const [mudando, setMudando] = useState<Canal | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.canais'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void carregar(); }, 150);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando os canais" />;

  const fazer = async (motivo: string) => {
    if (!pedido) return;
    const { canal, tipo } = pedido;
    const rota = tipo === 'desligar' ? 'desligar' : tipo === 'desconectar' ? 'desconectar' : 'ligar';
    const corpo = tipo === 'teste' ? { motivo, situacao: 'teste' } : { motivo };
    const r = await api<{ canais: Canal[]; pessoas: Pessoa[] }>(
      `/comercial/ajustes/canais/${canal.codigo}/${rota}`, { metodo: 'POST', corpo });
    setDado(r);
    setPedido(null);
    setOk(tipo === 'desconectar' ? `Pedido de desconexão do ${canal.nome} gravado: a mensageria faz em até um minuto, e o QR novo aparece aqui.`
      : tipo === 'desligar' ? `${canal.nome} fora do ar.` : tipo === 'teste' ? `${canal.nome} em teste.` : `${canal.nome} no ar.`);
  };

  const janela = pedido && ({
    no_ar: { titulo: 'Pôr no ar', acao: 'Pôr no ar', perigo: false,
      explica: <p>O {pedido.canal.nome} passa a receber e enviar mensagens de clientes. Diga por que ele entra no ar agora.</p> },
    teste: { titulo: 'Pôr em teste', acao: 'Pôr em teste', perigo: false,
      explica: <p>Em teste, a mensageria já conecta o {pedido.canal.nome}: use com o número de teste, antes do go-live.</p> },
    desligar: { titulo: 'Tirar do ar', acao: 'Tirar do ar', perigo: true,
      explica: <p>O {pedido.canal.nome} para de receber e de enviar. O que já está na caixa fica; o que chegar enquanto ele estiver fora não entra.</p> },
    desconectar: { titulo: 'Desconectar o aparelho', acao: 'Desconectar o aparelho', perigo: true,
      explica: (
        <p>O celular ligado ao {pedido.canal.nome} sai, e o canal volta a pedir o QR: alguém lê o QR novo com o
          celular do número certo. Enquanto ninguém lê, as mensagens esperam no WhatsApp.</p>) },
  } as const)[pedido.tipo];

  return (
    <>
      <AtualizacaoPausada estado={tempoReal} />
      <RespostaDaAcao erro={null} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      <ul className="ajc-canais">
        {dado.canais.map((c) => {
          const sit = degrauDe(SITUACAO_DO_CANAL, c.situacao);
          const con = degrauDe(CONEXAO_DO_CANAL, c.situacaoConexao);
          const conecta = c.tipo === 'whatsapp' || c.tipo === 'email';
          const doQr = c.tipo === 'whatsapp' && c.conexao === 'qr';
          /* m-6: conectado, mas sem o número do celular conferido com o do canal — não passa calado. */
          const conferir = doQr && c.situacao !== 'desligado' && c.situacaoConexao === 'conectado' && !!c.identificador
            && c.aparelho?.conferido !== true;
          return (
            <li key={c.codigo} className="cartao ajc-canal" data-canal={c.codigo}>
              <div className="ajc-canal-cabeca">
                <div className="ajc-canal-nome">
                  <h2>{c.nome}</h2>
                  <span className="ajc-aux">{TIPO_DO_CANAL[c.tipo] ?? c.tipo} · {CONEXAO[c.conexao] ?? c.conexao}
                    {c.visibilidade === 'carteira' ? ' · carteira' : ' · equipe'}</span>
                </div>
                <div className="ajc-selos">
                  <Selo tom={sit.tom}>{sit.texto}</Selo>
                  {conecta && c.situacao !== 'desligado' && <Selo tom={con.tom}>{con.texto}</Selo>}
                  {conferir && <Selo tom="atencao">Confira o número do aparelho</Selo>}
                </div>
              </div>
              <dl className="ajc-pares">
                {conecta && (
                  <div><dt>{c.tipo === 'email' ? 'Endereço' : 'Número'}</dt>
                    <dd>{c.identificador ? (c.tipo === 'whatsapp' ? telefoneLegivel(c.identificador) : c.identificador)
                      : 'ainda não informado'}</dd></div>
                )}
                {c.visibilidade === 'carteira' && (
                  <div><dt>Dono da carteira</dt><dd>{c.dono?.nome ?? 'sem dono (o canal só liga com dono)'}</dd></div>
                )}
                <div><dt>Agente de IA</dt><dd>{c.agenteAtivo ? 'atua neste canal' : 'não atua neste canal'}</dd></div>
                {doQr && c.situacao !== 'desligado' && c.situacaoConexao === 'conectado' && (
                  <div className="ajc-aparelho"><dt>Celular conectado</dt>
                    <dd>{c.aparelho?.telefone ? telefoneLegivel(c.aparelho.telefone) : 'o WhatsApp não disse o número'}</dd></div>
                )}
                {c.enviaAvisos && <div><dt>Avisos</dt><dd>os avisos da equipe pelo WhatsApp saem por aqui</dd></div>}
              </dl>
              {conferir && (
                <p className="ajc-qr-espera">Não deu para conferir que o celular conectado é o
                  {' '}{telefoneLegivel(c.identificador)}. Confira no próprio celular (WhatsApp › Configurações ›
                  Perfil) antes de usar o canal; se for outro número, use «Desconectar o aparelho» e leia o QR de novo.</p>
              )}

              {doQr && c.aguardaQr && c.situacao !== 'desligado' && (
                c.qr ? (
                  <div className="ajc-qr">
                    <img src={`data:image/png;base64,${c.qr.imagem}`} alt={`QR para conectar o ${c.nome}`} width={232} height={232} />
                    <div>
                      <p className="ajc-qr-passos">Abra o WhatsApp no celular › Aparelhos conectados › Conectar aparelho</p>
                      <p className="ajc-aux">Aponte a câmera para o QR. Ele muda a cada 20 segundos, e esta tela acompanha
                        sozinha. Use o celular do número {c.identificador ? telefoneLegivel(c.identificador) : 'do canal'}.</p>
                    </div>
                  </div>
                ) : (
                  <p className="ajc-qr-espera">O canal espera a leitura do QR. O QR aparece para quem liga os canais: peça a
                    eles para ler com o celular do número.</p>
                )
              )}
              {c.desconexao && (
                <p className="ajc-qr-espera">Desconexão do aparelho pedida por {c.desconexao.pedidaPor} em
                  {' '}{dataHora(c.desconexao.pedidaEm)}: a mensageria faz em até um minuto.</p>
              )}

              {(configura || liga) && (
                <div className="ajc-canal-acoes">
                  {configura && <button type="button" className="botao secundario" onClick={() => setMudando(c)}>Mudar…</button>}
                  {liga && c.situacao === 'desligado' && (
                    <button type="button" className="botao secundario" onClick={() => setPedido({ canal: c, tipo: 'no_ar' })}>Pôr no ar…</button>
                  )}
                  {liga && c.situacao === 'desligado' && conecta && (
                    <button type="button" className="botao secundario" onClick={() => setPedido({ canal: c, tipo: 'teste' })}>Pôr em teste…</button>
                  )}
                  {liga && c.situacao !== 'desligado' && (
                    <button type="button" className="botao secundario" onClick={() => setPedido({ canal: c, tipo: 'desligar' })}>Tirar do ar…</button>
                  )}
                  {liga && doQr && c.situacao !== 'desligado' && !c.desconexao
                    && ['conectado', 'falha', 'aguardando_qr'].includes(c.situacaoConexao) && (
                    <button type="button" className="botao discreto" onClick={() => setPedido({ canal: c, tipo: 'desconectar' })}>
                      Desconectar o aparelho…
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {janela && pedido && (
        <JanelaDoMotivo aberta titulo={janela.titulo} rotulo={pedido.canal.nome} explica={janela.explica}
          acao={janela.acao} perigo={janela.perigo} aoFechar={() => setPedido(null)} aoAgir={fazer} />
      )}
      <JanelaDoCanal canal={mudando} pessoas={dado.pessoas} aoFechar={() => setMudando(null)}
        aoGravar={(r) => { setDado(r); setMudando(null); setOk('Canal gravado.'); }} />
    </>
  );
}

/** O número (ou o endereço), o dono da carteira e o agente de um canal. */
function JanelaDoCanal({ canal, pessoas, aoFechar, aoGravar }: {
  canal: Canal | null; pessoas: Pessoa[]; aoFechar(): void; aoGravar(r: { canais: Canal[]; pessoas: Pessoa[] }): void;
}) {
  const { pode } = useSessao();
  const configuraAgente = pode('agente.configurar');
  const [identificador, setIdentificador] = useState('');
  const [dono, setDono] = useState('');
  const [agente, setAgente] = useState(false);
  const [avisos, setAvisos] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    if (!canal) return;
    setIdentificador(canal.identificador ?? '');
    setDono(canal.dono?.id ?? '');
    setAgente(canal.agenteAtivo);
    setAvisos(canal.enviaAvisos);
    setErro(null);
  }, [canal]);
  if (!canal) return null;
  const conecta = canal.tipo === 'whatsapp' || canal.tipo === 'email';
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    const corpo: Record<string, unknown> = {};
    if (conecta) corpo.identificador = identificador.trim() || null;
    if (canal.visibilidade === 'carteira') corpo.donoUsuarioId = dono || null;
    if (configuraAgente && agente !== canal.agenteAtivo) corpo.agenteAtivo = agente;
    if (canal.tipo === 'whatsapp' && avisos !== canal.enviaAvisos) corpo.enviaAvisos = avisos;
    try {
      aoGravar(await api(`/comercial/ajustes/canais/${canal.codigo}`, { metodo: 'PUT', corpo }));
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo="Mudar o canal" rotulo={canal.nome} aoFechar={aoFechar}>
      <div className="ajc-janela">
        {conecta && (
          <Campo rotulo={canal.tipo === 'email' ? 'Endereço da caixa' : 'Número do WhatsApp'} value={identificador}
            type={canal.tipo === 'email' ? 'email' : 'tel'} maxLength={200}
            onChange={(e) => setIdentificador(e.target.value)}
            dica={canal.tipo === 'email' ? 'O servidor e a senha ficam no servidor (mensageria.env), não aqui.'
              : 'Com o DDD, como (47) 99999-1234. O QR lido por outro número é recusado.'} />
        )}
        {canal.visibilidade === 'carteira' && (
          <label className="campo">
            <span className="rotulo">Dono da carteira</span>
            <select value={dono} onChange={(e) => setDono(e.target.value)}>
              <option value="">Sem dono</option>
              {pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
            <span className="dica">Só o dono e a diretoria veem as conversas da carteira.</span>
          </label>
        )}
        {configuraAgente && (
          <label className="ajc-marca">
            <input type="checkbox" checked={agente} onChange={(e) => setAgente(e.target.checked)} />
            O agente de IA atua neste canal
          </label>
        )}
        {canal.tipo === 'whatsapp' && (
          <label className="ajc-marca">
            <input type="checkbox" checked={avisos} onChange={(e) => setAvisos(e.target.checked)} />
            Os avisos da equipe pelo WhatsApp saem por este canal
          </label>
        )}
        {canal.tipo === 'whatsapp' && avisos && !canal.enviaAvisos && (
          <p className="ajc-aux">Um canal só manda os avisos: gravar tira os avisos do outro canal. Os que esperavam
            lá ficam lá, e não saem por este.</p>
        )}
        {erro ? <ErroDaApi erro={erro} /> : null}
      </div>
      <Rodape acao="Gravar" falta={null} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
    </Dialogo>
  );
}

// ── Rotas e substitutos ───────────────────────────────────────────────

interface Rota { codigo: string; nome: string; comercial: boolean; responsavel: Pessoa | null; substituto: Pessoa | null }

function AbaRotas() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, setDado, erro } = useCarga<{ rotas: Rota[]; pessoas: Pessoa[] }>('/comercial/ajustes/rotas');
  const [edit, setEdit] = useState<{ [codigo: string]: { r: string; s: string } }>({});
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    if (dado) setEdit(Object.fromEntries(dado.rotas.map((r) => [r.codigo, { r: r.responsavel?.id ?? '', s: r.substituto?.id ?? '' }])));
  }, [dado]);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando as rotas" />;
  const mudadas = dado.rotas.filter((r) => edit[r.codigo]
    && (edit[r.codigo].r !== (r.responsavel?.id ?? '') || edit[r.codigo].s !== (r.substituto?.id ?? '')));
  const gravar = async () => {
    setOcupado(true); setAcaoErro(null); setOk(null);
    try {
      setDado(await api('/comercial/ajustes/rotas', { metodo: 'PUT', corpo: {
        rotas: mudadas.map((r) => ({ codigo: r.codigo, responsavelUsuarioId: edit[r.codigo].r || null,
          substitutoUsuarioId: edit[r.codigo].s || null })) } }));
      setOk(`${plural(mudadas.length, 'rota gravada', 'rotas gravadas')}.`);
    } catch (e) { setAcaoErro(e); } finally { setOcupado(false); }
  };
  const nomeDe = (id: string) => dado.pessoas.find((p) => p.id === id)?.nome ?? '';
  return (
    <Cartao titulo="Rotas e substitutos"
      frase="Cada assunto vai ao responsável; se ele não assumir a conversa em 30 minutos úteis, ela passa ao substituto.">
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      <ul className="ajc-linhas">
        {dado.rotas.map((r) => {
          const e = edit[r.codigo] ?? { r: '', s: '' };
          return (
            <li key={r.codigo} className="ajc-rota" data-rota={r.codigo}>
              <div className="ajc-rota-nome">
                <strong>{r.nome}</strong>
                <span className="ajc-aux">{r.comercial ? 'gera oportunidade' : 'não gera oportunidade'}</span>
                {!e.r && <Selo tom="atencao">Defina o responsável</Selo>}
              </div>
              {configura ? (
                <>
                  <label className="campo">
                    <span className="rotulo">Responsável</span>
                    <select value={e.r} onChange={(x) => setEdit({ ...edit, [r.codigo]: { ...e, r: x.target.value } })}>
                      <option value="">Ninguém</option>
                      {dado.pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </label>
                  <label className="campo">
                    <span className="rotulo">Substituto</span>
                    <select value={e.s} onChange={(x) => setEdit({ ...edit, [r.codigo]: { ...e, s: x.target.value } })}>
                      <option value="">Sem substituto</option>
                      {dado.pessoas.filter((p) => p.id !== e.r).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <span className="ajc-valor"><span className="ajc-aux">Responsável</span>{nomeDe(e.r) || r.responsavel?.nome || '—'}</span>
                  <span className="ajc-valor"><span className="ajc-aux">Substituto</span>{r.substituto?.nome ?? 'sem substituto'}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {configura && <Gravar mudou={mudadas.length > 0} ocupado={ocupado} aoGravar={() => void gravar()} />}
    </Cartao>
  );
}

// ── Horário ───────────────────────────────────────────────────────────

const DIAS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

function AbaHorario() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, setDado, erro } = useCarga<{ dias: { diaSemana: number; inicio: string; fim: string }[] }>('/comercial/ajustes/horario');
  const [dias, setDias] = useState<{ ativo: boolean; inicio: string; fim: string }[]>([]);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const deDado = useCallback((d: typeof dado) => DIAS.map((_, i) => {
    const x = d?.dias.find((y) => y.diaSemana === i + 1);
    return { ativo: !!x, inicio: x?.inicio ?? '08:00', fim: x?.fim ?? '18:00' };
  }), []);
  useEffect(() => { if (dado) setDias(deDado(dado)); }, [dado, deDado]);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado || !dias.length) return <Carregando texto="Carregando o horário" />;
  const mudou = JSON.stringify(dias) !== JSON.stringify(deDado(dado));
  const invalidos = dias.filter((d) => d.ativo && !(d.inicio < d.fim)).length;
  const nenhum = !dias.some((d) => d.ativo);
  const gravar = async () => {
    if (invalidos || nenhum) {
      setAcaoErro(new Error(nenhum ? 'O horário não foi gravado: pelo menos um dia tem horário comercial.'
        : 'O horário não foi gravado: em cada dia, o início vem antes do fim. Confira os dias marcados.'));
      return;
    }
    setOcupado(true); setAcaoErro(null); setOk(null);
    try {
      setDado(await api('/comercial/ajustes/horario', { metodo: 'PUT', corpo: {
        dias: dias.map((d, i) => ({ ...d, diaSemana: i + 1 })).filter((d) => d.ativo)
          .map(({ diaSemana, inicio, fim }) => ({ diaSemana, inicio, fim })) } }));
      setOk('Horário gravado: a próxima conversa já conta com ele.');
    } catch (e) { setAcaoErro(e); } finally { setOcupado(false); }
  };
  return (
    <Cartao titulo="Horário comercial"
      frase="Os 30 minutos do substituto e o silêncio dos avisos contam só neste horário. Os feriados do município do laboratório não contam. Vale na hora.">
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      <ul className="ajc-linhas">
        {dias.map((d, i) => (
          <li key={DIAS[i]} className="ajc-dia" data-dia={i + 1}>
            <label className="ajc-marca">
              <input type="checkbox" checked={d.ativo} disabled={!configura}
                onChange={(e) => setDias(dias.map((x, j) => (j === i ? { ...x, ativo: e.target.checked } : x)))} />
              {DIAS[i]}
            </label>
            {d.ativo ? (
              <>
                <Campo rotulo="Início" type="time" value={d.inicio} disabled={!configura}
                  onChange={(e) => setDias(dias.map((x, j) => (j === i ? { ...x, inicio: e.target.value } : x)))} />
                <Campo rotulo="Fim" type="time" value={d.fim} disabled={!configura}
                  erro={d.inicio < d.fim ? undefined : 'O fim vem depois do início.'}
                  onChange={(e) => setDias(dias.map((x, j) => (j === i ? { ...x, fim: e.target.value } : x)))} />
              </>
            ) : <span className="ajc-aux">sem atendimento</span>}
          </li>
        ))}
      </ul>
      {configura && <Gravar mudou={mudou} ocupado={ocupado} aoGravar={() => void gravar()} />}
    </Cartao>
  );
}

// ── Números internos ──────────────────────────────────────────────────

interface Numero { id?: string; usuario: Pessoa; whatsapp: string | null; whatsappLid: string | null; ativo: boolean }

function AbaNumeros() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, setDado, erro } = useCarga<{ numeros: Numero[]; pessoas: Pessoa[] }>('/comercial/ajustes/numeros-internos');
  const [linhas, setLinhas] = useState<{ id?: string; usuarioId: string; whatsapp: string; ativo: boolean; lid: string | null }[]>([]);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const deDado = useCallback((d: typeof dado) => (d?.numeros ?? []).map((n) => ({
    id: n.id, usuarioId: n.usuario.id, whatsapp: n.whatsapp ?? '', ativo: n.ativo, lid: n.whatsappLid,
  })), []);
  useEffect(() => { if (dado) setLinhas(deDado(dado)); }, [dado, deDado]);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando os números internos" />;
  const mudou = JSON.stringify(linhas) !== JSON.stringify(deDado(dado));
  const muda = (i: number, x: Partial<(typeof linhas)[number]>) => setLinhas(linhas.map((l, j) => (j === i ? { ...l, ...x } : l)));
  const gravar = async () => {
    if (linhas.some((l) => !l.usuarioId || (!l.whatsapp.trim() && !l.lid))) {
      setAcaoErro(new Error('Os números não foram gravados: cada linha precisa da pessoa e do WhatsApp. Complete ou tire a linha vazia.'));
      return;
    }
    setOcupado(true); setAcaoErro(null); setOk(null);
    try {
      setDado(await api('/comercial/ajustes/numeros-internos', { metodo: 'PUT', corpo: {
        numeros: linhas.map((l) => ({ ...(l.id ? { id: l.id } : {}), usuarioId: l.usuarioId,
          whatsapp: l.whatsapp.trim() || null, whatsappLid: l.lid, ativo: l.ativo })) } }));
      setOk('Números internos gravados.');
    } catch (e) { setAcaoErro(e); } finally { setOcupado(false); }
  };
  return (
    <Cartao titulo="Números internos"
      frase="O WhatsApp pessoal de quem encaminha leads ao central: o que chega deles vira instrução, que só a pessoa e a diretoria veem."
      acoes={configura ? (
        <button type="button" className="botao secundario"
          onClick={() => setLinhas([...linhas, { usuarioId: '', whatsapp: '', ativo: true, lid: null }])}>
          Acrescentar número
        </button>) : undefined}>
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      {linhas.length === 0 ? <Vazio>Nenhum número interno ainda.</Vazio> : (
        <ul className="ajc-linhas">
          {linhas.map((l, i) => (
            <li key={l.id ?? `novo-${i}`} className="ajc-numero">
              <label className="campo">
                <span className="rotulo">Pessoa</span>
                <select value={l.usuarioId} disabled={!configura} onChange={(e) => muda(i, { usuarioId: e.target.value })}>
                  <option value="">Escolha a pessoa</option>
                  {dado.pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </label>
              <Campo rotulo="WhatsApp" type="tel" value={l.whatsapp} disabled={!configura} maxLength={40}
                placeholder="(47) 99999-1234" onChange={(e) => muda(i, { whatsapp: e.target.value })} />
              <label className="ajc-marca">
                <input type="checkbox" checked={l.ativo} disabled={!configura} onChange={(e) => muda(i, { ativo: e.target.checked })} />
                Ativo
              </label>
            </li>
          ))}
        </ul>
      )}
      {configura && <Gravar mudou={mudou} ocupado={ocupado} aoGravar={() => void gravar()} />}
    </Cartao>
  );
}

// ── Respostas prontas ─────────────────────────────────────────────────

interface Resposta { id: string; titulo: string; texto: string; atalho: string | null; ativa: boolean }

function AbaRespostas() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, erro, carregar } = useCarga<Resposta[]>('/comercial/respostas-prontas?todas=1');
  const [editando, setEditando] = useState<Resposta | 'nova' | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando as respostas prontas" />;
  return (
    <Cartao titulo="Respostas prontas"
      frase="Na conversa, «/» abre a lista: o atalho ou o título filtram, e Enter põe o texto."
      acoes={configura ? <button type="button" className="botao secundario" onClick={() => setEditando('nova')}>Nova resposta pronta</button> : undefined}>
      <RespostaDaAcao erro={null} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      {dado.length === 0 ? <Vazio>Nenhuma resposta pronta ainda.</Vazio> : (
        <ul className="ajc-linhas">
          {dado.map((r) => (
            <li key={r.id} className={`ajc-resposta${r.ativa ? '' : ' inativa'}`}>
              <div className="ajc-resposta-texto">
                <strong>{r.titulo}</strong>
                <span className="ajc-aux">{r.atalho ? `/${r.atalho}` : 'sem atalho'}{r.ativa ? '' : ' · desativada'}</span>
                <span className="ajc-previa">{r.texto}</span>
              </div>
              {configura && <button type="button" className="botao discreto" onClick={() => setEditando(r)}>Mudar…</button>}
            </li>
          ))}
        </ul>
      )}
      <JanelaDaResposta resposta={editando} aoFechar={() => setEditando(null)}
        aoGravar={() => { setEditando(null); setOk('Resposta pronta gravada.'); void carregar(); }} />
    </Cartao>
  );
}

function JanelaDaResposta({ resposta, aoFechar, aoGravar }: { resposta: Resposta | 'nova' | null; aoFechar(): void; aoGravar(): void }) {
  const [titulo, setTitulo] = useState('');
  const [atalho, setAtalho] = useState('');
  const [texto, setTexto] = useState('');
  const [ativa, setAtiva] = useState(true);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    if (!resposta) return;
    const r = resposta === 'nova' ? null : resposta;
    setTitulo(r?.titulo ?? ''); setAtalho(r?.atalho ?? ''); setTexto(r?.texto ?? ''); setAtiva(r?.ativa ?? true);
    setErro(null);
  }, [resposta]);
  if (!resposta) return null;
  const falta = faltaPara('gravar', [...(titulo.trim() ? [] : ['o título']), ...(texto.trim() ? [] : ['o texto'])]);
  const gravar = async () => {
    setOcupado(true); setErro(null);
    const corpo = { titulo: titulo.trim(), texto: texto.trim(), atalho: atalho.trim() || null };
    try {
      if (resposta === 'nova') await api('/comercial/respostas-prontas', { metodo: 'POST', corpo });
      else await api(`/comercial/respostas-prontas/${resposta.id}`, { metodo: 'PATCH', corpo: { ...corpo, ativa } });
      aoGravar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo={resposta === 'nova' ? 'Nova resposta pronta' : 'Mudar a resposta pronta'} aoFechar={aoFechar}>
      <div className="ajc-janela">
        <Campo rotulo="Título" value={titulo} maxLength={120} onChange={(e) => setTitulo(e.target.value)} />
        <Campo rotulo="Atalho" value={atalho} maxLength={30} onChange={(e) => setAtalho(e.target.value)}
          dica="Letras, números, - ou _, sem a barra: «orcamento» vira /orcamento." />
        <Area rotulo="Texto" value={texto} rows={5} maxLength={4000} onChange={(e) => setTexto(e.target.value)} />
        {resposta !== 'nova' && (
          <label className="ajc-marca">
            <input type="checkbox" checked={ativa} onChange={(e) => setAtiva(e.target.checked)} /> Ativa (aparece no «/»)
          </label>
        )}
        {erro ? <ErroDaApi erro={erro} /> : null}
      </div>
      <Rodape acao="Gravar" falta={falta} ocupado={ocupado} aoCancelar={aoFechar} aoAgir={() => void gravar()} />
    </Dialogo>
  );
}

// ── Estimativa ────────────────────────────────────────────────────────

interface Regra { linha: string; descricao: string; metrosPorFuro: number; precoPorMetro: number; mobilizacao: number; ativa: boolean }

const LINHA: { readonly [l: string]: string } = {
  sondagem: 'Sondagem', laboratorio: 'Laboratório', controle_tecnologico: 'Controle tecnológico',
  investigacao_combinada: 'Investigação combinada', projetos_consultoria: 'Projetos e consultoria', outro: 'Outro',
};
const decimal = (t: string) => Number(t.replace(/\./g, '').replace(',', '.'));
const emTexto = (n: number) => String(n).replace('.', ',');

function AbaEstimativa() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, setDado, erro } = useCarga<{ regras: Regra[]; linhas: string[]; corte: number | null }>('/comercial/ajustes/estimativa');
  type Linha = { linha: string; descricao: string; metros: string; preco: string; mob: string; ativa: boolean };
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const deDado = useCallback((d: typeof dado): Linha[] => (d?.regras ?? []).map((r) => ({
    linha: r.linha, descricao: r.descricao, metros: emTexto(r.metrosPorFuro), preco: emTexto(r.precoPorMetro),
    mob: emTexto(r.mobilizacao), ativa: r.ativa,
  })), []);
  useEffect(() => { if (dado) setLinhas(deDado(dado)); }, [dado, deDado]);
  const faltantes = useMemo(() => (dado?.linhas ?? []).filter((l) => !linhas.some((x) => x.linha === l)), [dado, linhas]);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando a estimativa" />;
  const mudou = JSON.stringify(linhas) !== JSON.stringify(deDado(dado));
  const muda = (i: number, x: Partial<Linha>) => setLinhas(linhas.map((l, j) => (j === i ? { ...l, ...x } : l)));
  const gravar = async () => {
    const ruim = linhas.find((l) => !(decimal(l.metros) > 0) || !(decimal(l.preco) >= 0) || !(decimal(l.mob) >= 0)
      || l.descricao.trim().length < 3);
    if (ruim) {
      setAcaoErro(new Error(`A estimativa não foi gravada: confira «${LINHA[ruim.linha] ?? ruim.linha}» — a descrição, os metros `
        + 'por furo (mais que zero) e os valores (números, sem negativo).'));
      return;
    }
    setOcupado(true); setAcaoErro(null); setOk(null);
    try {
      setDado(await api('/comercial/ajustes/estimativa', { metodo: 'PUT', corpo: {
        regras: linhas.map((l) => ({ linha: l.linha, descricao: l.descricao.trim(), metrosPorFuro: decimal(l.metros),
          precoPorMetro: decimal(l.preco), mobilizacao: decimal(l.mob), ativa: l.ativa })) } }));
      setOk('Estimativa gravada: a próxima oportunidade já usa a regra nova.');
    } catch (e) { setAcaoErro(e); } finally { setOcupado(false); }
  };
  return (
    <Cartao titulo="Estimativa do roteamento"
      frase={<>Furos × metros por furo × preço do metro + mobilização. Só para o roteamento: nunca vai ao cliente.
        {dado.corte != null && <> Acima de {reais(dado.corte)}, o pedido vai a «grande ou estratégico».</>}</>}
      acoes={configura && faltantes.length ? (
        <button type="button" className="botao secundario"
          onClick={() => setLinhas([...linhas, { linha: faltantes[0], descricao: LINHA[faltantes[0]] ?? faltantes[0],
            metros: '1', preco: '0', mob: '0', ativa: true }])}>
          Acrescentar regra
        </button>) : undefined}>
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {!configura && <p className="ajc-so-ver">{SO_VE}</p>}
      {linhas.length === 0 ? <Vazio>Nenhuma regra: sem ela, o roteamento usa só o valor que a pessoa escrever.</Vazio> : (
        <ul className="ajc-linhas">
          {linhas.map((l, i) => (
            <li key={l.linha} className="ajc-regra" data-linha={l.linha}>
              <div className="ajc-regra-cabeca">
                {configura && !dado.regras.some((r) => r.linha === l.linha) ? (
                  <label className="campo">
                    <span className="rotulo">Linha de serviço</span>
                    <select value={l.linha} onChange={(e) => muda(i, { linha: e.target.value })}>
                      {[l.linha, ...faltantes].map((x) => <option key={x} value={x}>{LINHA[x] ?? x}</option>)}
                    </select>
                  </label>
                ) : <strong>{LINHA[l.linha] ?? l.linha}</strong>}
                <label className="ajc-marca">
                  <input type="checkbox" checked={l.ativa} disabled={!configura} onChange={(e) => muda(i, { ativa: e.target.checked })} />
                  Ativa
                </label>
              </div>
              <Campo rotulo="Descrição" value={l.descricao} disabled={!configura} maxLength={300}
                onChange={(e) => muda(i, { descricao: e.target.value })} />
              <div className="ajc-regra-numeros">
                <Campo rotulo="Metros por furo" inputMode="decimal" value={l.metros} disabled={!configura}
                  onChange={(e) => muda(i, { metros: e.target.value })} />
                <Campo rotulo="Preço do metro (R$)" inputMode="decimal" value={l.preco} disabled={!configura}
                  onChange={(e) => muda(i, { preco: e.target.value })} />
                <Campo rotulo="Mobilização (R$)" inputMode="decimal" value={l.mob} disabled={!configura}
                  onChange={(e) => muda(i, { mob: e.target.value })} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {configura && <Gravar mudou={mudou} ocupado={ocupado} aoGravar={() => void gravar()} />}
    </Cartao>
  );
}

// ── Estratégicos ──────────────────────────────────────────────────────

interface Estrategico { id: string; nome: string; razaoSocial: string; estrategicoEm?: string; estrategicoPor?: string; estrategico?: boolean }

function AbaEstrategicos() {
  const [texto, setTexto] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { const t = window.setTimeout(() => setQ(texto.trim()), 300); return () => window.clearTimeout(t); }, [texto]);
  const { dado, erro, carregar } = useCarga<{ estrategicos: Estrategico[]; achados: Estrategico[] }>(
    `/comercial/ajustes/estrategicos${q.length >= 2 ? `?busca=${encodeURIComponent(q)}` : ''}`);
  const [marcando, setMarcando] = useState<{ c: Estrategico; marcar: boolean } | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando os estratégicos" />;
  const achados = dado.achados.filter((a) => !a.estrategico);
  return (
    <Cartao titulo="Clientes estratégicos"
      frase="O pedido de um cliente estratégico vai sempre a «grande ou estratégico», qualquer que seja o valor.">
      <RespostaDaAcao erro={null} ok={ok} />
      {dado.estrategicos.length === 0 ? <Vazio>Nenhum cliente estratégico ainda.</Vazio> : (
        <ul className="ajc-linhas">
          {dado.estrategicos.map((c) => (
            <li key={c.id} className="ajc-cliente">
              <div><strong>{c.nome}</strong>
                <span className="ajc-aux">marcado por {c.estrategicoPor ?? '—'} em {dataHora(c.estrategicoEm)}</span></div>
              <button type="button" className="botao secundario" onClick={() => setMarcando({ c, marcar: false })}>Desmarcar…</button>
            </li>
          ))}
        </ul>
      )}
      <div className="ajc-busca">
        <Campo rotulo="Marcar outro cliente" type="search" value={texto} placeholder="Nome ou razão social" maxLength={120}
          onChange={(e) => setTexto(e.target.value)} />
      </div>
      {q.length >= 2 && (achados.length === 0 ? <Vazio>Nenhum cliente com «{q}» fora da lista.</Vazio> : (
        <ul className="ajc-linhas">
          {achados.map((c) => (
            <li key={c.id} className="ajc-cliente">
              <div><strong>{c.nome}</strong>{c.razaoSocial !== c.nome && <span className="ajc-aux">{c.razaoSocial}</span>}</div>
              <button type="button" className="botao secundario" onClick={() => setMarcando({ c, marcar: true })}>Marcar…</button>
            </li>
          ))}
        </ul>
      ))}
      {marcando && (
        <JanelaDoMotivo aberta titulo={marcando.marcar ? 'Marcar como estratégico' : 'Desmarcar o estratégico'}
          rotulo={marcando.c.nome} acao={marcando.marcar ? 'Marcar' : 'Desmarcar'}
          explica={<p>{marcando.marcar ? 'Os pedidos deste cliente passam a ir sempre a «grande ou estratégico».'
            : 'Os pedidos deste cliente voltam a seguir o valor estimado.'}</p>}
          aoFechar={() => setMarcando(null)}
          aoAgir={async (motivo) => {
            await api(`/clientes/${marcando.c.id}/estrategico`, { metodo: 'PATCH', corpo: { estrategico: marcando.marcar, motivo } });
            setOk(marcando.marcar ? `${marcando.c.nome} é estratégico.` : `${marcando.c.nome} deixou de ser estratégico.`);
            setMarcando(null);
            void carregar();
          }} />
      )}
    </Cartao>
  );
}

// ── Contatos com o mesmo telefone ─────────────────────────────────────

interface Grupo { whatsapp: string; contatos: { id: string; nome: string; telefone: string; cliente: string | null; obra: string | null }[] }

function AbaRepetidos() {
  const { pode } = useSessao();
  const responde = pode('atendimento.responder');
  const { dado, erro, carregar } = useCarga<{ grupos: Grupo[] }>('/comercial/ajustes/contatos-repetidos');
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando os contatos" />;
  const definir = async (g: Grupo, id: string, nome: string) => {
    setAcaoErro(null); setOk(null);
    try {
      await api(`/comercial/contatos/${id}`, { metodo: 'PATCH', corpo: { whatsapp: g.whatsapp } });
      setOk(`O WhatsApp ${telefoneLegivel(g.whatsapp)} é de ${nome}.`);
      void carregar();
    } catch (e) { setAcaoErro(e); }
  };
  return (
    <Cartao titulo="Contatos com o mesmo telefone"
      frase="O cadastro tem mais de um contato com o mesmo celular, e a mensagem que chega dele não sabe de quem é. Escolha de quem é o WhatsApp.">
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {dado.grupos.length === 0 ? <Vazio>Nenhum celular repetido no cadastro.</Vazio> : (
        <ul className="ajc-grupos">
          {dado.grupos.map((g) => (
            <li key={g.whatsapp} className="ajc-grupo">
              <strong>{telefoneLegivel(g.whatsapp)}</strong>
              <ul className="ajc-linhas">
                {g.contatos.map((c) => (
                  <li key={c.id} className="ajc-repetido" data-contato={c.id}>
                    <div><span>{c.nome}</span>
                      <span className="ajc-aux">{[c.cliente, c.obra, c.telefone].filter(Boolean).join(' · ')}</span></div>
                    {responde && (
                      <button type="button" className="botao secundario" onClick={() => void definir(g, c.id, c.nome)}>É deste contato</button>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

// ── Entradas com falha ────────────────────────────────────────────────

interface Falha {
  id: string; tipo: string; recebidaEm: string; tentativas: number; canal: { codigo: string; nome: string };
  remetente: string | null; remetenteNome: string | null;
}

const TIPO_DA_ENTRADA: { readonly [t: string]: string } = {
  texto: 'texto', audio: 'áudio', imagem: 'imagem', arquivo: 'arquivo', email: 'e-mail', outro: 'outro tipo',
};

function AbaFalhas() {
  const { pode } = useSessao();
  const configura = pode('atendimento.configurar');
  const { dado, setDado, erro } = useCarga<{ entradas: Falha[] }>('/comercial/ajustes/entradas-com-falha');
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (erro && !dado) return <ErroDaApi erro={erro} />;
  if (!dado) return <Carregando texto="Carregando as entradas" />;
  const reprocessar = async (f: Falha) => {
    setAcaoErro(null); setOk(null);
    try {
      await api(`/comercial/ajustes/entradas-com-falha/${f.id}/reprocessar`, { metodo: 'POST' });
      setDado({ entradas: dado.entradas.filter((x) => x.id !== f.id) });
      setOk('A mensagem voltou à fila: ela entra na caixa em instantes.');
    } catch (e) { setAcaoErro(e); }
  };
  return (
    <Cartao titulo="Entradas com falha"
      frase="Mensagens que chegaram e não entraram na caixa, nem depois de três novas tentativas. Estão guardadas: confira o canal e reprocesse.">
      <RespostaDaAcao erro={acaoErro} ok={ok} />
      {dado.entradas.length === 0 ? <Vazio>Nenhuma mensagem parada: tudo o que chegou entrou na caixa.</Vazio> : (
        <ul className="ajc-linhas">
          {dado.entradas.map((f) => (
            <li key={f.id} className="ajc-falha" data-entrada={f.id}>
              <div>
                <span>{f.remetenteNome || telefoneLegivel(f.remetente?.replace(/@.*$/, '')) || 'canal de carteira'}</span>
                <span className="ajc-aux">{f.canal.nome} · {TIPO_DA_ENTRADA[f.tipo] ?? f.tipo} · chegou em {dataHora(f.recebidaEm)}
                  {' '}· {plural(f.tentativas, 'tentativa', 'tentativas')}</span>
              </div>
              {configura && <button type="button" className="botao secundario" onClick={() => void reprocessar(f)}>Reprocessar</button>}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}
